/**
 * TAMS Application Theme
 * Modern Corporate Theme - Professional Blue Palette
 */
import { createTheme, alpha } from "@mui/material/styles";
import { tokens } from "./Theme/tokens";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: tokens.colors.primary[600],      // #0A5DC2 - Professional blue
      light: tokens.colors.primary[400],
      dark: tokens.colors.primary[800],
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: tokens.colors.neutral[600],      // #475569 - Neutral gray
      light: tokens.colors.neutral[400],
      dark: tokens.colors.neutral[800],
      contrastText: "#FFFFFF",
    },
    background: {
      default: tokens.colors.neutral[50],    // #F8FAFC - Page background
      paper: "#FFFFFF",                       // Card surface
    },
    text: {
      primary: tokens.colors.neutral[800],   // #1E293B
      secondary: tokens.colors.neutral[600], // #475569
      disabled: tokens.colors.neutral[400],  // #94A3B8
    },
    error: {
      main: tokens.colors.error.main,
      light: tokens.colors.error.light,
      dark: tokens.colors.error.dark,
      contrastText: tokens.colors.error.contrastText,
    },
    warning: {
      main: tokens.colors.warning.main,
      light: tokens.colors.warning.light,
      dark: tokens.colors.warning.dark,
      contrastText: tokens.colors.warning.contrastText,
    },
    success: {
      main: tokens.colors.success.main,
      light: tokens.colors.success.light,
      dark: tokens.colors.success.dark,
      contrastText: tokens.colors.success.contrastText,
    },
    info: {
      main: tokens.colors.info.main,
      light: tokens.colors.info.light,
      dark: tokens.colors.info.dark,
      contrastText: tokens.colors.info.contrastText,
    },
    divider: tokens.colors.neutral[200],     // #E2E8F0 - Light borders
    action: {
      active: tokens.colors.neutral[600],
      hover: alpha(tokens.colors.primary[600], 0.04),
      selected: alpha(tokens.colors.primary[600], 0.08),
      disabled: tokens.colors.neutral[300],
      disabledBackground: tokens.colors.neutral[100],
    },
  },

  typography: {
    fontFamily: tokens.typography.fontFamily.sans,
    h1: {
      fontSize: tokens.typography.fontSize["4xl"],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
      letterSpacing: tokens.typography.letterSpacing.tight,
      color: tokens.colors.neutral[900],
    },
    h2: {
      fontSize: tokens.typography.fontSize["3xl"],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.tight,
      letterSpacing: tokens.typography.letterSpacing.tight,
      color: tokens.colors.neutral[900],
    },
    h3: {
      fontSize: tokens.typography.fontSize["2xl"],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.tight,
      color: tokens.colors.neutral[800],
    },
    h4: {
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[800],
    },
    h5: {
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[800],
    },
    h6: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[700],
    },
    subtitle1: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[700],
    },
    subtitle2: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[600],
    },
    body1: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.relaxed,
      color: tokens.colors.neutral[700],
    },
    body2: {
      fontSize: tokens.typography.fontSize.xs,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[600],
    },
    button: {
      textTransform: "none",
      fontWeight: tokens.typography.fontWeight.medium,
      fontSize: tokens.typography.fontSize.sm,
    },
    caption: {
      fontSize: tokens.typography.fontSize.xs,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.neutral[500],
    },
    overline: {
      fontSize: "0.625rem",
      fontWeight: tokens.typography.fontWeight.semibold,
      letterSpacing: tokens.typography.letterSpacing.wide,
      textTransform: "uppercase",
      color: tokens.colors.neutral[500],
    },
  },

  shape: {
    borderRadius: 8,
  },

  shadows: [
    "none",
    tokens.shadows.sm,
    tokens.shadows.sm,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.modal,
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: "thin",
          scrollbarColor: `${tokens.colors.neutral[300]} transparent`,
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: tokens.colors.neutral[300],
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: tokens.colors.neutral[400],
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: tokens.shadows.sm,
          backgroundColor: "#FFFFFF",
          color: tokens.colors.neutral[800],
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          padding: "8px 16px",
          fontWeight: tokens.typography.fontWeight.medium,
          fontSize: tokens.typography.fontSize.sm,
          transition: tokens.transitions.fast,
          textTransform: "none",
        },
        contained: {
          "&:hover": {
            boxShadow: tokens.shadows.md,
          },
        },
        containedPrimary: {
          backgroundColor: tokens.colors.primary[600],
          "&:hover": {
            backgroundColor: tokens.colors.primary[700],
          },
        },
        containedSecondary: {
          backgroundColor: tokens.colors.neutral[100],
          color: tokens.colors.neutral[700],
          "&:hover": {
            backgroundColor: tokens.colors.neutral[200],
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
            backgroundColor: alpha(tokens.colors.primary[600], 0.04),
          },
        },
        outlinedPrimary: {
          borderColor: tokens.colors.primary[600],
          color: tokens.colors.primary[600],
          "&:hover": {
            borderColor: tokens.colors.primary[700],
            backgroundColor: alpha(tokens.colors.primary[600], 0.04),
          },
        },
        text: {
          "&:hover": {
            backgroundColor: alpha(tokens.colors.primary[600], 0.04),
          },
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: tokens.typography.fontSize.xs,
        },
        sizeLarge: {
          padding: "12px 24px",
          fontSize: tokens.typography.fontSize.base,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          transition: tokens.transitions.fast,
          "&:hover": {
            backgroundColor: alpha(tokens.colors.primary[600], 0.08),
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.card,
          border: `1px solid ${tokens.colors.neutral[200]}`,
          transition: tokens.transitions.shadow,
          "&:hover": {
            boxShadow: tokens.shadows.cardHover,
          },
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "16px 20px 12px",
        },
        title: {
          fontSize: tokens.typography.fontSize.base,
          fontWeight: tokens.typography.fontWeight.semibold,
        },
        subheader: {
          fontSize: tokens.typography.fontSize.sm,
          color: tokens.colors.neutral[500],
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "16px 20px",
          "&:last-child": {
            paddingBottom: "20px",
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.lg,
        },
        elevation1: {
          boxShadow: tokens.shadows.card,
        },
        elevation2: {
          boxShadow: tokens.shadows.md,
        },
        elevation3: {
          boxShadow: tokens.shadows.lg,
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: tokens.borderRadius.md,
            backgroundColor: "#FFFFFF",
            transition: tokens.transitions.colors,
            "& fieldset": {
              borderColor: tokens.colors.neutral[300],
              transition: tokens.transitions.colors,
            },
            "&:hover fieldset": {
              borderColor: tokens.colors.primary[400],
            },
            "&.Mui-focused fieldset": {
              borderWidth: "2px",
              borderColor: tokens.colors.primary[600],
            },
          },
        },
      },
    },

    MuiInputBase: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          fontSize: tokens.typography.fontSize.sm,
        },
        input: {
          padding: "10px 12px",
          "&::placeholder": {
            color: tokens.colors.neutral[400],
            opacity: 1,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: tokens.borderRadius.md,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.borderRadius.md,
          boxShadow: tokens.shadows.dropdown,
          border: `1px solid ${tokens.colors.neutral[200]}`,
          marginTop: "4px",
        },
        listbox: {
          padding: "4px",
        },
        option: {
          borderRadius: tokens.borderRadius.sm,
          margin: "2px 0",
          "&:hover": {
            backgroundColor: tokens.colors.neutral[100],
          },
          '&[aria-selected="true"]': {
            backgroundColor: alpha(tokens.colors.primary[600], 0.08),
          },
        },
        tag: {
          borderRadius: tokens.borderRadius.sm,
          backgroundColor: tokens.colors.primary[50],
          color: tokens.colors.primary[700],
          border: `1px solid ${tokens.colors.primary[200]}`,
        },
      },
    },

    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          fontWeight: tokens.typography.fontWeight.medium,
          fontSize: tokens.typography.fontSize.xs,
        },
        colorPrimary: {
          backgroundColor: tokens.colors.primary[50],
          color: tokens.colors.primary[700],
          border: `1px solid ${tokens.colors.primary[200]}`,
        },
        colorSuccess: {
          backgroundColor: tokens.colors.success.light,
          color: tokens.colors.success.dark,
        },
        colorError: {
          backgroundColor: tokens.colors.error.light,
          color: tokens.colors.error.dark,
        },
        colorWarning: {
          backgroundColor: tokens.colors.warning.light,
          color: tokens.colors.warning.dark,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.borderRadius.xl,
          boxShadow: tokens.shadows.modal,
          padding: "8px",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: tokens.typography.fontSize.lg,
          fontWeight: tokens.typography.fontWeight.semibold,
          padding: "20px 24px 12px",
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "12px 24px 20px",
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "12px 24px 20px",
          gap: "8px",
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.medium,
          backgroundColor: tokens.colors.neutral[800],
          borderRadius: tokens.borderRadius.md,
          padding: "6px 12px",
        },
        arrow: {
          color: tokens.colors.neutral[800],
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.dropdown,
          border: `1px solid ${tokens.colors.neutral[200]}`,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.dropdown,
          border: `1px solid ${tokens.colors.neutral[200]}`,
          marginTop: "4px",
        },
        list: {
          padding: "4px",
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.sm,
          margin: "2px 4px",
          padding: "8px 12px",
          fontSize: tokens.typography.fontSize.sm,
          transition: tokens.transitions.fast,
          "&:hover": {
            backgroundColor: tokens.colors.neutral[100],
          },
          "&.Mui-selected": {
            backgroundColor: alpha(tokens.colors.primary[600], 0.08),
            "&:hover": {
              backgroundColor: alpha(tokens.colors.primary[600], 0.12),
            },
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "44px",
        },
        indicator: {
          height: "3px",
          borderRadius: "3px 3px 0 0",
          backgroundColor: tokens.colors.primary[600],
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: tokens.typography.fontWeight.medium,
          fontSize: tokens.typography.fontSize.sm,
          minHeight: "44px",
          padding: "12px 16px",
          transition: tokens.transitions.fast,
          "&.Mui-selected": {
            color: tokens.colors.primary[600],
          },
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.colors.neutral[50],
          "& .MuiTableCell-head": {
            fontWeight: tokens.typography.fontWeight.semibold,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.neutral[600],
            textTransform: "uppercase",
            letterSpacing: tokens.typography.letterSpacing.wide,
            borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: tokens.transitions.fast,
          "&:hover": {
            backgroundColor: tokens.colors.neutral[50],
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.colors.neutral[100]}`,
          padding: "12px 16px",
          fontSize: tokens.typography.fontSize.sm,
        },
      },
    },

    // Note: MuiDataGrid requires @mui/x-data-grid type augmentation
    // DataGrid styles are handled via CSS in App.css

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          fontSize: tokens.typography.fontSize.sm,
        },
        standardSuccess: {
          backgroundColor: tokens.colors.success.light,
          color: tokens.colors.success.dark,
        },
        standardError: {
          backgroundColor: tokens.colors.error.light,
          color: tokens.colors.error.dark,
        },
        standardWarning: {
          backgroundColor: tokens.colors.warning.light,
          color: tokens.colors.warning.dark,
        },
        standardInfo: {
          backgroundColor: tokens.colors.info.light,
          color: tokens.colors.info.dark,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.colors.primary[100],
          color: tokens.colors.primary[700],
          fontWeight: tokens.typography.fontWeight.semibold,
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: tokens.typography.fontWeight.semibold,
          fontSize: "0.65rem",
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.full,
          backgroundColor: tokens.colors.neutral[200],
        },
        bar: {
          borderRadius: tokens.borderRadius.full,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: tokens.colors.primary[600],
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.colors.neutral[200],
        },
        rounded: {
          borderRadius: tokens.borderRadius.md,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.colors.neutral[200],
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          transition: tokens.transitions.fast,
          "&:hover": {
            backgroundColor: tokens.colors.neutral[100],
          },
          "&.Mui-selected": {
            backgroundColor: alpha(tokens.colors.primary[600], 0.08),
            color: tokens.colors.primary[700],
            "&:hover": {
              backgroundColor: alpha(tokens.colors.primary[600], 0.12),
            },
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: {
          padding: 0,
          margin: 2,
          transitionDuration: "300ms",
          "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor: tokens.colors.primary[600],
              opacity: 1,
              border: 0,
            },
          },
        },
        thumb: {
          boxSizing: "border-box",
          width: 22,
          height: 22,
        },
        track: {
          borderRadius: 26 / 2,
          backgroundColor: tokens.colors.neutral[300],
          opacity: 1,
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: tokens.colors.neutral[400],
          "&.Mui-checked": {
            color: tokens.colors.primary[600],
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: tokens.colors.neutral[400],
          "&.Mui-checked": {
            color: tokens.colors.primary[600],
          },
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: tokens.typography.fontSize.sm,
        },
        separator: {
          color: tokens.colors.neutral[400],
        },
      },
    },

    MuiStepper: {
      styleOverrides: {
        root: {
          padding: "24px 0",
        },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: tokens.typography.fontWeight.medium,
          "&.Mui-active": {
            color: tokens.colors.primary[600],
            fontWeight: tokens.typography.fontWeight.semibold,
          },
          "&.Mui-completed": {
            color: tokens.colors.success.main,
            fontWeight: tokens.typography.fontWeight.medium,
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: tokens.colors.neutral[300],
          "&.Mui-active": {
            color: tokens.colors.primary[600],
          },
          "&.Mui-completed": {
            color: tokens.colors.success.main,
          },
        },
      },
    },
  },
});

export default theme;
